// /app/frontend/src/pages/ClientPortalPage.js
/**
 * Zone Client Sécurisée - Page publique accessible avec token + mot de passe
 * Permet aux clients de:
 * - Voir les détails de l'événement
 * - Télécharger les documents des food trucks (carte identité, KBIS, RC Pro, etc.)
 * - Attacher le menu du jour
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, 
  Download, 
  MapPin, 
  Calendar, 
  Users, 
  ChefHat,
  Shield,
  AlertCircle,
  CheckCircle,
  Loader2,
  FileImage,
  IdCard,
  Building2,
  Car,
  ClipboardList,
  Lock,
  Eye,
  EyeOff,
  KeyRound,
  Camera,
  Image,
  Trash2,
  Upload,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientPortalPage = () => {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');
  const token = searchParams.get('token');
  
  // Auth states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [linkInfo, setLinkInfo] = useState(null);
  
  // Content states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [menuContent, setMenuContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  
  // Photos states
  const [photos, setPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  
  useEffect(() => {
    if (!eventId || !token) {
      setError('Lien invalide. Veuillez utiliser le lien complet fourni.');
      setLoading(false);
      return;
    }
    
    verifyLink();
  }, [eventId, token]);
  
  // Step 1: Verify the link is valid
  const verifyLink = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await fetch(
        `${API_URL}/api/client-portal/verify-link/${eventId}?token=${encodeURIComponent(token)}`
      );
      
      if (!res.ok) {
        if (res.status === 403) {
          throw new Error('Lien invalide ou expiré. Veuillez contacter l\'organisateur.');
        }
        throw new Error('Erreur lors de la vérification du lien');
      }
      
      const data = await res.json();
      setLinkInfo(data);
      
      // If already authenticated (same session), load data directly
      if (data.is_authenticated) {
        setIsAuthenticated(true);
        loadEventData();
      }
    } catch (err) {
      console.error('Error verifying link:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Step 2: Authenticate with password
  const handleAuthenticate = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      toast.error('Veuillez entrer le mot de passe');
      return;
    }
    
    try {
      setAuthenticating(true);
      
      const res = await fetch(
        `${API_URL}/api/client-portal/authenticate/${eventId}?token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: password.toUpperCase() })
        }
      );
      
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error('Mot de passe incorrect');
        }
        throw new Error('Erreur lors de l\'authentification');
      }
      
      toast.success('Authentification réussie');
      setIsAuthenticated(true);
      loadEventData();
    } catch (err) {
      console.error('Auth error:', err);
      toast.error(err.message);
    } finally {
      setAuthenticating(false);
    }
  };
  
  // Step 3: Load event data after authentication
  const loadEventData = async () => {
    try {
      setLoading(true);
      
      // Load event details
      const eventRes = await fetch(
        `${API_URL}/api/client-portal/event/${eventId}?token=${encodeURIComponent(token)}`
      );
      
      if (!eventRes.ok) {
        if (eventRes.status === 401) {
          setIsAuthenticated(false);
          throw new Error('Session expirée. Veuillez vous reconnecter.');
        }
        throw new Error('Erreur lors du chargement des données');
      }
      
      const eventData = await eventRes.json();
      setEvent(eventData);
      setMenuContent(eventData.client_menu || '');
      
      // Load food trucks
      const ftRes = await fetch(
        `${API_URL}/api/client-portal/event/${eventId}/food-trucks?token=${encodeURIComponent(token)}`
      );
      
      if (ftRes.ok) {
        const ftData = await ftRes.json();
        setFoodTrucks(ftData.food_trucks || []);
      }
      
      // Load photos
      const photosRes = await fetch(
        `${API_URL}/api/client-portal/event/${eventId}/photos?token=${encodeURIComponent(token)}`
      );
      
      if (photosRes.ok) {
        const photosData = await photosRes.json();
        setPhotos(photosData.photos || []);
      }
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDownloadDocument = async (ftId, docType, ftName) => {
    try {
      const res = await fetch(
        `${API_URL}/api/client-portal/event/${eventId}/food-truck/${ftId}/document/${docType}?token=${encodeURIComponent(token)}`
      );
      
      if (!res.ok) {
        throw new Error('Document non disponible');
      }
      
      const data = await res.json();
      
      if (data.file_url) {
        window.open(`${API_URL}${data.file_url}`, '_blank');
      }
    } catch (err) {
      toast.error(err.message || 'Erreur lors du téléchargement');
    }
  };
  
  const handleSaveMenu = async () => {
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('menu_content', menuContent);
      formData.append('menu_type', 'texte');
      
      const res = await fetch(
        `${API_URL}/api/client-portal/event/${eventId}/menu?token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!res.ok) throw new Error('Erreur lors de la sauvegarde');
      
      toast.success('Menu enregistré avec succès');
      loadEventData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };
  
  const handleUploadMenuFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Utilisez PDF, JPG ou PNG.');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10MB)');
      return;
    }
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(
        `${API_URL}/api/client-portal/event/${eventId}/menu-file?token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!res.ok) throw new Error('Erreur lors de l\'upload');
      
      toast.success('Menu uploadé avec succès');
      loadEventData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };
  
  // Upload photos
  const handleUploadPhotos = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // Validate files
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    const validFiles = files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Type non autorisé`);
        return false;
      }
      if (file.size > 15 * 1024 * 1024) {
        toast.error(`${file.name}: Fichier trop volumineux (max 15MB)`);
        return false;
      }
      return true;
    });
    
    if (!validFiles.length) return;
    
    try {
      setUploadingPhotos(true);
      
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const res = await fetch(
        `${API_URL}/api/client-portal/event/${eventId}/photos?token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!res.ok) throw new Error('Erreur lors de l\'upload');
      
      const data = await res.json();
      toast.success(data.message || 'Photos uploadées avec succès');
      
      // Clear file input
      e.target.value = '';
      
      // Reload photos
      loadEventData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploadingPhotos(false);
    }
  };
  
  // Delete photo
  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/client-portal/event/${eventId}/photos/${photoId}?token=${encodeURIComponent(token)}`,
        { method: 'DELETE' }
      );
      
      if (!res.ok) throw new Error('Erreur lors de la suppression');
      
      toast.success('Photo supprimée');
      setPhotos(photos.filter(p => p.id !== photoId));
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  
  const getDocumentIcon = (docType) => {
    switch (docType) {
      case 'carte_identite': return <IdCard className="h-4 w-4" />;
      case 'carte_commercant': return <Building2 className="h-4 w-4" />;
      case 'kbis': return <FileText className="h-4 w-4" />;
      case 'assurance_rc_pro': return <Shield className="h-4 w-4" />;
      case 'assurance_vehicule': return <Car className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };
  
  const getDocumentLabel = (docType) => {
    switch (docType) {
      case 'carte_identite': return 'Carte d\'identité';
      case 'carte_commercant': return 'Carte commerçant';
      case 'kbis': return 'KBIS';
      case 'assurance_rc_pro': return 'Assurance RC Pro';
      case 'assurance_vehicule': return 'Assurance véhicule';
      default: return docType;
    }
  };
  
  // Loading state
  if (loading && !linkInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#8B1538] mx-auto mb-4" />
          <p className="text-gray-600">Vérification du lien...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error && !linkInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Accès Refusé</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <p className="text-sm text-gray-500">
              Si vous pensez qu'il s'agit d'une erreur, veuillez contacter l'organisateur de l'événement.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Password form (before authentication)
  if (!isAuthenticated && linkInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardHeader className="text-center bg-[#8B1538] text-white rounded-t-lg">
            <div className="flex items-center justify-center gap-2 mb-2">
              <ChefHat className="h-8 w-8" />
              <span className="text-2xl font-bold">Food Truck United</span>
            </div>
            <CardDescription className="text-white/80">
              Espace Client Sécurisé
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <Lock className="h-16 w-16 text-[#8B1538] mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {linkInfo.event_titre}
              </h2>
              <p className="text-gray-600">
                Entrez le mot de passe fourni par l'organisateur
              </p>
            </div>
            
            <form onSubmit={handleAuthenticate} className="space-y-4">
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value.toUpperCase())}
                  placeholder="Mot de passe (6 caractères)"
                  className="pl-10 pr-10 text-center text-2xl tracking-widest uppercase font-mono"
                  maxLength={6}
                  autoFocus
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              
              <Button
                type="submit"
                className="w-full bg-[#8B1538] hover:bg-[#6B1028]"
                disabled={authenticating || password.length < 6}
                data-testid="submit-password-btn"
              >
                {authenticating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Accéder à l'espace client
                  </>
                )}
              </Button>
            </form>
            
            <p className="text-xs text-gray-500 text-center mt-4">
              Le mot de passe vous a été communiqué par l'organisateur de l'événement.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Loading after authentication
  if (loading && isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#8B1538] mx-auto mb-4" />
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }
  
  // Main content (after authentication)
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-[#8B1538] text-white py-6 shadow-lg">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <ChefHat className="h-8 w-8" />
            <span className="text-2xl font-bold">Food Truck United</span>
          </div>
          <div className="flex items-center gap-2 text-white/80">
            <Shield className="h-4 w-4" />
            <span>Espace Client Sécurisé</span>
            <Badge variant="secondary" className="bg-green-500/20 text-green-100 ml-2">
              <CheckCircle className="h-3 w-3 mr-1" />
              Authentifié
            </Badge>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Event Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{event?.titre}</h1>
          <div className="flex items-center gap-4 text-gray-600 flex-wrap">
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {event?.lieu}
            </span>
            {event?.date_debut && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(event.date_debut)}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {foodTrucks.length} Food Truck(s)
            </span>
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-xl">
            <TabsTrigger value="info" data-testid="tab-info">
              <FileText className="h-4 w-4 mr-2" />
              Informations
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">
              <Download className="h-4 w-4 mr-2" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="menu" data-testid="tab-menu">
              <ClipboardList className="h-4 w-4 mr-2" />
              Menu
            </TabsTrigger>
            <TabsTrigger value="photos" data-testid="tab-photos">
              <Camera className="h-4 w-4 mr-2" />
              Photos
              {photos.length > 0 && (
                <Badge className="ml-1 bg-[#8B1538] text-white text-xs">{photos.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          {/* Info Tab */}
          <TabsContent value="info">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-[#8B1538]" />
                    Détails de l'événement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Lieu</label>
                    <p className="text-gray-900">{event?.lieu}</p>
                    {event?.adresse && <p className="text-gray-600 text-sm">{event.adresse}</p>}
                  </div>
                  
                  {event?.dates && event.dates.length > 0 ? (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Dates</label>
                      <div className="space-y-1 mt-1">
                        {event.dates.map((d, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Badge variant="outline">{formatDate(d.date)}</Badge>
                            <span className="text-sm text-gray-600">
                              {d.services?.map(s => s.type === 'midi' ? 'Midi' : 'Soir').join(' + ')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : event?.date_debut && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Date</label>
                      <p className="text-gray-900">{formatDate(event.date_debut)}</p>
                    </div>
                  )}
                  
                  {event?.couverts_estimes && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Couverts estimés</label>
                      <p className="text-gray-900">{event.couverts_estimes} personnes</p>
                    </div>
                  )}
                  
                  {event?.description && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Description</label>
                      <p className="text-gray-900 whitespace-pre-wrap">{event.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-[#8B1538]" />
                    Food Trucks participants
                  </CardTitle>
                  <CardDescription>
                    {foodTrucks.length} food truck(s) confirmé(s)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {foodTrucks.map((ft) => (
                      <div 
                        key={ft.id} 
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{ft.nom_food_truck}</p>
                          <p className="text-sm text-gray-600">
                            {ft.specialite || ft.specialites?.join(', ') || 'Spécialité non renseignée'}
                          </p>
                        </div>
                        {ft.has_documents && (
                          <Badge variant="secondary" className="bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Docs
                          </Badge>
                        )}
                      </div>
                    ))}
                    
                    {foodTrucks.length === 0 && (
                      <p className="text-gray-500 text-center py-4">
                        Aucun food truck confirmé pour le moment
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5 text-[#8B1538]" />
                  Documents des Food Trucks
                </CardTitle>
                <CardDescription>
                  Téléchargez les documents administratifs des food trucks participants
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {foodTrucks.map((ft) => (
                    <div key={ft.id} className="border rounded-lg p-4">
                      <h3 className="font-semibold text-lg text-gray-900 mb-3">
                        {ft.nom_food_truck}
                      </h3>
                      
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {['carte_identite', 'carte_commercant', 'kbis', 'assurance_rc_pro', 'assurance_vehicule'].map((docType) => {
                          const hasDoc = ft.documents?.[docType];
                          
                          return (
                            <Button
                              key={docType}
                              variant={hasDoc ? "outline" : "ghost"}
                              className={`justify-start ${!hasDoc ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#8B1538]/10'}`}
                              onClick={() => hasDoc && handleDownloadDocument(ft.id, docType, ft.nom_food_truck)}
                              disabled={!hasDoc}
                              data-testid={`download-${docType}-${ft.id}`}
                            >
                              {getDocumentIcon(docType)}
                              <span className="ml-2 truncate">{getDocumentLabel(docType)}</span>
                              {hasDoc && <Download className="h-3 w-3 ml-auto" />}
                            </Button>
                          );
                        })}
                      </div>
                      
                      {!ft.has_documents && (
                        <p className="text-sm text-amber-600 mt-3 flex items-center gap-1">
                          <AlertCircle className="h-4 w-4" />
                          Aucun document disponible pour ce food truck
                        </p>
                      )}
                    </div>
                  ))}
                  
                  {foodTrucks.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Aucun food truck à afficher</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Menu Tab */}
          <TabsContent value="menu">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-[#8B1538]" />
                  Menu du jour de l'événement
                </CardTitle>
                <CardDescription>
                  Partagez le menu prévu pour cet événement avec les food trucks
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Text Menu */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Menu (texte)
                  </label>
                  <Textarea
                    value={menuContent}
                    onChange={(e) => setMenuContent(e.target.value)}
                    placeholder="Décrivez le menu prévu pour l'événement...&#10;&#10;Exemple:&#10;- Entrée: Salade composée&#10;- Plat: Burger gourmet / Végétarien&#10;- Dessert: Brownie maison"
                    rows={8}
                    className="w-full"
                    data-testid="menu-textarea"
                  />
                  <Button 
                    onClick={handleSaveMenu}
                    disabled={uploading}
                    className="mt-3 bg-[#8B1538] hover:bg-[#6B1028]"
                    data-testid="save-menu-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Enregistrer le menu
                      </>
                    )}
                  </Button>
                </div>
                
                <div className="border-t pt-6">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Ou uploader un fichier (PDF, image)
                  </label>
                  <div className="flex items-center gap-4">
                    <Input
                      type="file"
                      accept=".pdf,image/jpeg,image/png,image/webp"
                      onChange={handleUploadMenuFile}
                      disabled={uploading}
                      className="max-w-xs"
                      data-testid="menu-file-input"
                    />
                    {event?.client_menu_filename && (
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        <FileImage className="h-3 w-3 mr-1" />
                        {event.client_menu_filename}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Formats acceptés: PDF, JPG, PNG. Taille max: 10MB
                  </p>
                </div>
                
                {event?.client_menu_updated_at && (
                  <p className="text-sm text-gray-500">
                    Dernière mise à jour: {new Date(event.client_menu_updated_at).toLocaleString('fr-FR')}
                    {event.client_menu_updated_by && ` par ${event.client_menu_updated_by}`}
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Photos Tab */}
          <TabsContent value="photos">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-5 w-5 text-[#8B1538]" />
                  Photos de l'événement
                </CardTitle>
                <CardDescription>
                  Partagez des photos avec l'organisateur (lieu, installation, etc.)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Upload Section */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#8B1538] transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    onChange={handleUploadPhotos}
                    disabled={uploadingPhotos}
                    className="hidden"
                    id="photo-upload"
                    data-testid="photo-upload-input"
                  />
                  <label 
                    htmlFor="photo-upload" 
                    className="cursor-pointer block"
                  >
                    {uploadingPhotos ? (
                      <>
                        <Loader2 className="h-12 w-12 mx-auto mb-3 text-[#8B1538] animate-spin" />
                        <p className="text-gray-600">Upload en cours...</p>
                      </>
                    ) : (
                      <>
                        <Upload className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-gray-600 font-medium">Cliquez pour ajouter des photos</p>
                        <p className="text-sm text-gray-500 mt-1">ou glissez-déposez vos fichiers ici</p>
                        <p className="text-xs text-gray-400 mt-2">JPG, PNG, WebP, HEIC - Max 15MB par photo</p>
                      </>
                    )}
                  </label>
                </div>
                
                {/* Photos Grid */}
                {photos.length > 0 ? (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-700">
                        {photos.length} photo(s) uploadée(s)
                      </h4>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {photos.map((photo) => (
                        <div 
                          key={photo.id} 
                          className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border"
                        >
                          <img
                            src={`${API_URL}${photo.url}`}
                            alt={photo.filename}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = '';
                              e.target.className = 'hidden';
                            }}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => window.open(`${API_URL}${photo.url}`, '_blank')}
                                className="bg-white/90"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeletePhoto(photo.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 truncate">
                            {photo.filename}
                            <span className="ml-2 text-gray-300">({formatFileSize(photo.size)})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Image className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p>Aucune photo uploadée</p>
                    <p className="text-sm mt-1">Ajoutez des photos pour les partager avec l'organisateur</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-400">
            Food Truck United - Espace Client Sécurisé
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Ce lien est personnel et confidentiel. Ne le partagez pas.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ClientPortalPage;
